<script setup>
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import {
  ref,
  computed,
  h,
  shallowRef,
  useTemplateRef,
  watch,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useOperators } from 'dashboard/components-next/filter/operators';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import {
  generateAutomationPayload,
  getActionIcon,
  getAttributes,
} from 'dashboard/helper/automationHelper';
import { getAttributeIcon } from 'dashboard/components-next/filter/helper/filterAttributeIcons';
import { provideDropdownTeleport } from 'dashboard/components-next/dropdown-menu/base/provider';
import { isEmptyValue, validateAutomation } from 'dashboard/helper/validations';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  captainAssignmentSummary,
  pinnedInboxIds,
  hasCaptainSelection,
} from 'dashboard/helper/captainAssignment';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import {
  AUTOMATION_RULE_EVENTS,
  AUTOMATION_ACTION_TYPES,
  DEFAULT_DELAY_MINUTES,
} from './constants';
import AutomationRunTypeSelector from './components/AutomationRunTypeSelector.vue';
import AutomationWaitCondition from './components/AutomationWaitCondition.vue';
import AutomationInstantTrigger from './components/AutomationInstantTrigger.vue';
import AutomationActions from './components/AutomationActions.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['save']);

const DATE_SCHEDULE_ATTRIBUTE_TYPES = new Set(['date', 'datetime']);

const automation = defineModel('automation', { type: Object, default: null });
const automationUIFlags = useMapGetter('automations/getUIFlags');
const isSaving = computed(() =>
  Boolean(
    automationUIFlags.value?.isCreating || automationUIFlags.value?.isUpdating
  )
);
const currentInboxIds = computed(() =>
  pinnedInboxIds(automation.value?.conditions || [])
);
const actionDropdownValues = type =>
  props.getActionDropdownValues(type, currentInboxIds.value);
const formRef = ref(null);
const scrollToError = async () => {
  await nextTick();
  formRef.value
    ?.querySelector(
      '[data-error="true"], [role="alert"], input.error, .text-n-ruby-11'
    )
    ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
};
const captainInboxes = useMapGetter('inboxes/getInboxes');
const captainSummary = action =>
  captainAssignmentSummary(
    automation.value?.conditions || [],
    [action],
    captainInboxes.value || []
  );
const captainSummaryHasIssues = action =>
  captainSummary(action).some(line => line.status !== 'ok');

const INPUT_TYPE_MAP = {
  multi_select: 'multiSelect',
  search_select: 'searchSelect',
  plain_text: 'plainText',
  multi_text: 'multiText',
  date: 'date',
  datetime: 'datetime',
};

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();
const { operators } = useOperators();
const customAttributes = useMapGetter('attributes/getAttributes');

provideDropdownTeleport();

const panelRef = ref(null);
const instantTriggerRef = useTemplateRef('instantTriggerRef');
const waitConditionRef = useTemplateRef('waitConditionRef');
const errors = ref({});
// Findings from the server-side linter. Errors block the save (impossible
// condition, unknown attribute); warnings do not (another rule fights this one).
const lintErrors = ref([]);
const lintWarnings = ref([]);

const isEditMode = computed(() => props.mode === 'edit');

const scheduleDateAttributeOptions = computed(() => {
  const options = (customAttributes.value || [])
    .filter(
      attr =>
        attr.attribute_model === 'conversation_attribute' &&
        DATE_SCHEDULE_ATTRIBUTE_TYPES.has(String(attr.attribute_display_type))
    )
    .map(attr => {
      const base = attr.attribute_display_name || attr.attribute_key;
      const label =
        attr.attribute_display_type === 'datetime'
          ? `${base} (${t('AUTOMATION.ADD.FORM.SCHEDULE.ATTRIBUTE_TYPE_DATETIME')})`
          : base;
      return {
        key: attr.attribute_key,
        label,
        type: attr.attribute_display_type,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));

  // Keep orphan keys from saved rules selectable (renamed/deleted CA).
  const currentKey = automation.value?.schedule?.attribute_key;
  if (currentKey && !options.some(option => option.key === currentKey)) {
    options.unshift({
      key: currentKey,
      label: t('AUTOMATION.ADD.FORM.SCHEDULE.ATTRIBUTE_KEY_ORPHAN', {
        key: currentKey,
      }),
      type: 'unknown',
    });
  }

  return options;
});

const allowsDelayedExecution = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.DELAYED_AUTOMATIONS)
);

// The wait lives here rather than in the wait section so that switching between the two run
// types doesn't discard a duration the user already typed in.
const isDelayed = ref(false);
const isSavedWait = ref(false);
const delayMinutes = ref(DEFAULT_DELAY_MINUTES);
const delayUnit = ref(DURATION_UNITS.HOURS);
const instantTriggerDraft = shallowRef(null);
const waitTriggerDraft = shallowRef(null);
const isSyncingDelayState = ref(false);
// Bumped on every open() so the wait section remounts and re-reads the rule it is given.
const waitSectionKey = ref(0);

const executionDelayInvalid = computed(
  () => isDelayed.value && !Number.isFinite(delayMinutes.value)
);

const statusOptions = computed(() =>
  (props.getConditionDropdownValues('status') || [])
    .filter(option => option.id !== 'all')
    .map(option => ({ value: option.id, label: option.name }))
);

const inboxOptions = computed(
  () => props.getConditionDropdownValues('inbox_id') || []
);

const cloneConditions = conditions => JSON.parse(JSON.stringify(conditions));

const captureTriggerDraft = () => {
  if (!automation.value) return null;

  return {
    eventName: automation.value.event_name,
    conditions: cloneConditions(automation.value.conditions),
  };
};

const restoreTriggerDraft = draft => {
  if (!automation.value || !draft) return;

  automation.value.event_name = draft.eventName;
  automation.value.conditions = cloneConditions(draft.conditions);
};

// Show the wait in the largest whole unit (240 min → 4 hours). The delay is passed in by open()
// rather than read from `automation`, whose model prop only settles a tick later.
const syncDelayState = executionDelay => {
  isSyncingDelayState.value = true;
  isDelayed.value = Boolean(executionDelay);
  isSyncingDelayState.value = false;
  isSavedWait.value = isEditMode.value && Boolean(executionDelay);
  const minutes = executionDelay || DEFAULT_DELAY_MINUTES;
  if (minutes % 1440 === 0) delayUnit.value = DURATION_UNITS.DAYS;
  else if (minutes % 60 === 0) delayUnit.value = DURATION_UNITS.HOURS;
  else delayUnit.value = DURATION_UNITS.MINUTES;
  delayMinutes.value = minutes;
  waitSectionKey.value += 1;

  // Drafts are captured when the run type actually changes, so they start empty: seeding them
  // here would read `automation` before its model prop settles and keep the previous rule.
  instantTriggerDraft.value = null;
  waitTriggerDraft.value = null;
};

watch(
  isDelayed,
  (nextIsDelayed, previousIsDelayed) => {
    if (isSyncingDelayState.value) return;

    const currentDraft = captureTriggerDraft();
    if (previousIsDelayed) waitTriggerDraft.value = currentDraft;
    else instantTriggerDraft.value = currentDraft;

    const nextDraft = nextIsDelayed
      ? waitTriggerDraft.value
      : instantTriggerDraft.value;
    restoreTriggerDraft(nextDraft);
    // A restored in-form wait draft must hydrate like a saved wait instead of being reset on mount.
    isSavedWait.value = nextIsDelayed && Boolean(nextDraft);
  },
  { flush: 'sync' }
);

watch([isDelayed, delayMinutes], () => {
  if (!automation.value || !allowsDelayedExecution.value) return;
  automation.value.execution_delay = isDelayed.value
    ? delayMinutes.value
    : null;
});

const titleKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.TITLE' : 'AUTOMATION.ADD.TITLE'
);
const cancelKey = computed(() =>
  isEditMode.value
    ? 'AUTOMATION.EDIT.CANCEL_BUTTON_TEXT'
    : 'AUTOMATION.ADD.CANCEL_BUTTON_TEXT'
);
const submitKey = computed(() =>
  isEditMode.value ? 'AUTOMATION.EDIT.SUBMIT' : 'AUTOMATION.ADD.SUBMIT'
);

const getTranslatedAttributes = (type, event) => {
  return getAttributes(type, event).map(attribute => {
    const skipTranslation =
      attribute.customAttributeType ||
      ['contact_custom_attribute', 'conversation_custom_attribute'].includes(
        attribute.key
      );
    return {
      ...attribute,
      name: skipTranslation
        ? attribute.name
        : t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
    };
  });
};

const eventName = computed(() => automation.value?.event_name);
const isTimeTriggered = computed(() => eventName.value === 'time_triggered');

const ensureSchedule = () => {
  if (!automation.value.schedule) {
    automation.value.schedule = {
      kind: 'hours_since_last_outgoing',
      hours: 24,
    };
  }
  if (
    automation.value.schedule.kind === 'days_since_attribute' &&
    !automation.value.schedule.relative_to
  ) {
    automation.value.schedule.relative_to = 'after';
  }
};

const scheduleRelativeTo = computed({
  get: () => automation.value?.schedule?.relative_to || 'after',
  set: value => {
    if (!automation.value.schedule) automation.value.schedule = {};
    automation.value.schedule.relative_to = value;
  },
});

const showScheduleDays = computed(
  () =>
    automation.value?.schedule?.kind === 'days_since_attribute' &&
    scheduleRelativeTo.value !== 'on'
);

watch(
  isTimeTriggered,
  enabled => {
    if (enabled) ensureSchedule();
  },
  { immediate: true }
);

watch(
  () => automation.value?.schedule?.kind,
  kind => {
    if (kind === 'days_since_attribute' && automation.value?.schedule) {
      if (!automation.value.schedule.relative_to) {
        automation.value.schedule.relative_to = 'after';
      }
    }
  }
);

const filterTypes = computed(() => {
  const event = eventName.value;
  if (!event || !props.automationTypes[event]) return [];

  const attributes = getTranslatedAttributes(props.automationTypes, event);

  return attributes.map(attr => {
    if (attr.disabled) {
      return { value: attr.key, label: attr.name, disabled: true };
    }

    const mappedInputType = INPUT_TYPE_MAP[attr.inputType] || 'plainText';
    const options = props.getConditionDropdownValues(attr.key) || [];

    const filterOperators = (attr.filterOperators || []).map(op => {
      const enriched = operators.value[op.value];
      if (enriched) return enriched;
      return {
        value: op.value,
        label: t(`FILTER.OPERATOR_LABELS.${op.value}`),
        hasInput: true,
        inputOverride: null,
        icon: h('span', { class: 'i-ph-equals-bold !text-n-blue-11' }),
      };
    });

    return {
      attributeKey: attr.key,
      value: attr.key,
      attributeName: attr.name,
      label: attr.name,
      icon: getAttributeIcon({
        attributeKey: attr.key,
        attributeDisplayType: attr.attributeDisplayType,
      }),
      inputType: mappedInputType,
      options,
      filterOperators,
      dataType: 'text',
      attributeModel: attr.customAttributeType || 'standard',
    };
  });
});

const automationRuleEvents = computed(() =>
  AUTOMATION_RULE_EVENTS.map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  }))
);

const hasAutomationMutated = computed(() => {
  const firstActionParams = automation.value?.actions[0]?.action_params;
  const hasActionParams = Array.isArray(firstActionParams)
    ? firstActionParams.length > 0
    : Boolean(
        firstActionParams &&
          typeof firstActionParams === 'object' &&
          Object.keys(firstActionParams).length
      );

  return Boolean(
    !isEmptyValue(automation.value?.conditions[0]?.values) || hasActionParams
  );
});

const automationActionTypes = computed(() => {
  const actionTypes = isCloudFeatureEnabled('sla')
    ? AUTOMATION_ACTION_TYPES
    : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

  return actionTypes.map(action => ({
    ...action,
    label: t(`AUTOMATION.ACTIONS.${action.label}`),
    icon: getActionIcon(action.key),
  }));
});

// Business-rule guards (Configuración → Reglas de negocio) only ever gate a
// status change — showing this toggle on a rule that only sends a message or
// adds a label would be a setting that does nothing, so it only appears when
// relevant.
const STATUS_CHANGING_ACTIONS = [
  'resolve_conversation',
  'open_conversation',
  'pending_conversation',
  'mute_conversation',
  'snooze_conversation',
  'change_status',
];
const hasStatusChangingAction = computed(() =>
  (automation.value.actions || []).some(action =>
    STATUS_CHANGING_ACTIONS.includes(action.action_name)
  )
);

watch(
  () => automation.value,
  () => {
    if (Object.keys(errors.value).length) {
      errors.value = {};
    }
  },
  { deep: true }
);

const isConditionsValid = () =>
  (isDelayed.value ? waitConditionRef : instantTriggerRef).value?.validate() ??
  true;

const resetValidation = () => {
  errors.value = {};
  lintErrors.value = [];
  lintWarnings.value = [];
  instantTriggerRef.value?.resetValidation();
  waitConditionRef.value?.resetValidation();
};

// Called by the parent after the API answers, so server-side findings land
// next to the field-level validation the form already does.
const setLintFindings = ({ errors: apiErrors = [], warnings = [] } = {}) => {
  lintErrors.value = apiErrors;
  lintWarnings.value = warnings;
  scrollToError();
};

// The server sends raw identifiers (assign_agent); show the same label the
// user picked in the Actions dropdown instead.
const translateActionName = actionName => {
  if (!actionName) return actionName;
  const key = `AUTOMATION.ACTIONS.${String(actionName).toUpperCase()}`;
  const translated = t(key);
  return translated === key ? actionName : translated;
};

const lintMessage = finding => {
  const key = `AUTOMATION.LINT.${String(finding.code || '').toUpperCase()}`;
  const meta = { ...(finding.meta || {}) };
  if (meta.action_name)
    meta.action_name = translateActionName(meta.action_name);
  const translated = t(key, meta);
  // Fall back to the raw code if this fork hasn't translated it yet — better
  // than showing an empty banner.
  return translated === key ? finding.code : translated;
};

const syncCustomAttributeTypes = () => {
  automation.value.conditions.forEach(condition => {
    const filterType = filterTypes.value.find(
      ft => ft.attributeKey === condition.attribute_key
    );
    condition.custom_attribute_type =
      filterType?.attributeModel === 'standard'
        ? ''
        : filterType?.attributeModel || '';
  });
};

const open = (executionDelay = null) => {
  resetValidation();
  syncDelayState(executionDelay);
  panelRef.value?.open();
};

const close = () => {
  resetValidation();
  panelRef.value?.close();
};

const emitSaveAutomation = () => {
  if (isSaving.value) return;
  // Previous server findings are stale the moment the user resubmits.
  lintErrors.value = [];
  lintWarnings.value = [];
  syncCustomAttributeTypes();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (allowsDelayedExecution.value && executionDelayInvalid.value) {
    errors.value.execution_delay = true;
  }
  if (Object.keys(errors.value).length === 0 && conditionsValid) {
    const payload = generateAutomationPayload(automation.value);
    // The API rejects the param when the feature is off; existing values are kept server-side.
    if (!allowsDelayedExecution.value) delete payload.execution_delay;
    emit('save', payload, props.mode);
  } else {
    scrollToError();
  }
};

defineExpose({ open, close, setLintFindings });
</script>

<template>
  <SidePanel ref="panelRef" width="3xl" :title="$t(titleKey)">
    <div v-if="automation" ref="formRef" class="flex flex-col w-full gap-6">
      <div class="flex flex-col gap-4">
        <Input
          v-model="automation.name"
          required
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :data-error="Boolean(errors.name)"
          :message-type="errors.name ? 'error' : 'info'"
          :message="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <Input
          v-model="automation.description"
          required
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :data-error="Boolean(errors.description)"
          :message-type="errors.description ? 'error' : 'info'"
          :message="
            errors.description ? $t('AUTOMATION.ADD.FORM.DESC.ERROR') : ''
          "
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />
      </div>
      <section v-if="isTimeTriggered" class="flex flex-col gap-3">
        <label>{{ $t('AUTOMATION.ADD.FORM.SCHEDULE.LABEL') }}</label>
        <div
          class="grid gap-3 p-3 outline outline-1 rounded-xl -outline-offset-1 outline-n-weak dark:outline-n-strong"
        >
          <label class="text-sm text-n-slate-11">
            {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.KIND') }}
            <ComboBox
              v-model="automation.schedule.kind"
              class="mt-1 w-full m-0"
              teleport
              :allow-deselect="false"
              :options="[
                {
                  value: 'days_since_attribute',
                  label: $t('AUTOMATION.ADD.FORM.SCHEDULE.KINDS.DAYS_SINCE'),
                },
                {
                  value: 'hours_since_last_outgoing',
                  label: $t(
                    'AUTOMATION.ADD.FORM.SCHEDULE.KINDS.HOURS_OUTGOING'
                  ),
                },
                {
                  value: 'hours_since_last_incoming',
                  label: $t(
                    'AUTOMATION.ADD.FORM.SCHEDULE.KINDS.HOURS_INCOMING'
                  ),
                },
              ]"
            />
          </label>
          <label
            v-if="automation.schedule.kind === 'days_since_attribute'"
            class="text-sm text-n-slate-11"
          >
            {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.ATTRIBUTE_KEY') }}
            <ComboBox
              v-model="automation.schedule.attribute_key"
              class="mt-1 w-full m-0"
              teleport
              :allow-deselect="false"
              :options="[
                {
                  value: '',
                  label: $t('AUTOMATION.ADD.FORM.SCHEDULE.ATTRIBUTE_KEY_EMPTY'),
                },
                ...scheduleDateAttributeOptions.map(attr => ({
                  value: attr.key,
                  label: attr.label,
                })),
              ]"
            />
            <p
              v-if="!scheduleDateAttributeOptions.length"
              class="m-0 mt-1 text-xs text-n-slate-10"
            >
              {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.ATTRIBUTE_KEY_NONE') }}
            </p>
          </label>
          <label
            v-if="automation.schedule.kind === 'days_since_attribute'"
            class="text-sm text-n-slate-11"
          >
            {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.RELATIVE_TO') }}
            <ComboBox
              v-model="scheduleRelativeTo"
              class="mt-1 w-full m-0"
              teleport
              :allow-deselect="false"
              :options="[
                {
                  value: 'after',
                  label: $t('AUTOMATION.ADD.FORM.SCHEDULE.RELATIVE_TO_AFTER'),
                },
                {
                  value: 'on',
                  label: $t('AUTOMATION.ADD.FORM.SCHEDULE.RELATIVE_TO_ON'),
                },
                {
                  value: 'before',
                  label: $t('AUTOMATION.ADD.FORM.SCHEDULE.RELATIVE_TO_BEFORE'),
                },
              ]"
            />
          </label>
          <label v-if="showScheduleDays" class="text-sm text-n-slate-11">
            {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.DAYS') }}
            <Input
              v-model="automation.schedule.days"
              type="number"
              min="1"
              class="mt-1 w-full"
            />
          </label>
          <p
            v-if="automation.schedule.kind === 'days_since_attribute'"
            class="m-0 text-xs text-n-slate-11"
          >
            {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.RELATIVE_HINT') }}
          </p>
          <label
            v-if="
              automation.schedule.kind === 'hours_since_last_outgoing' ||
              automation.schedule.kind === 'hours_since_last_incoming'
            "
            class="text-sm text-n-slate-11"
          >
            {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.HOURS') }}
            <Input
              v-model="automation.schedule.hours"
              type="number"
              min="1"
              class="mt-1 w-full"
            />
          </label>
          <p v-if="errors.schedule" class="m-0 text-sm text-n-ruby-11">
            {{
              $t(
                `AUTOMATION.ADD.FORM.SCHEDULE.ERRORS.${errors.schedule}`,
                errors.schedule
              )
            }}
          </p>
        </div>
        <p class="text-xs text-n-slate-11 m-0">
          {{ $t('AUTOMATION.ADD.FORM.SCHEDULE.HELP') }}
        </p>
      </section>
      <AutomationRunTypeSelector
        v-if="allowsDelayedExecution && !isTimeTriggered"
        v-model="isDelayed"
      />
      <AutomationWaitCondition
        v-if="isDelayed && !isTimeTriggered"
        :key="waitSectionKey"
        ref="waitConditionRef"
        v-model:event-name="automation.event_name"
        v-model:conditions="automation.conditions"
        v-model:delay="delayMinutes"
        v-model:unit="delayUnit"
        :status-options="statusOptions"
        :inbox-options="inboxOptions"
        :filter-types="filterTypes"
        :remove-filter="removeFilter"
        :is-saved-wait="isSavedWait"
        :has-error="Boolean(errors.execution_delay)"
      />
      <AutomationInstantTrigger
        v-else
        ref="instantTriggerRef"
        v-model:event-name="automation.event_name"
        v-model:conditions="automation.conditions"
        :events="automationRuleEvents"
        :filter-types="filterTypes"
        :errors="errors"
        :show-reset-message="!isEditMode && hasAutomationMutated"
        :append-new-condition="appendNewCondition"
        :remove-filter="removeFilter"
        :on-event-change="onEventChange"
      />
      <AutomationActions
        v-model="automation.actions"
        :action-types="automationActionTypes"
        :get-action-dropdown-values="actionDropdownValues"
        :files="automation.files"
        :show-file-name="isEditMode"
        :errors="errors"
        :append-new-action="appendNewAction"
        :remove-action="removeAction"
        :reset-action="resetAction"
      >
        <template #captain-summary="{ action }">
          <div
            v-if="hasCaptainSelection([action])"
            role="status"
            class="m-0 grid gap-1 rounded-lg p-3 text-sm"
            :class="
              captainSummaryHasIssues(action)
                ? 'bg-n-amber-2 text-n-amber-11'
                : 'bg-n-alpha-1 text-n-slate-12'
            "
            data-testid="captain-summary"
          >
            <p
              v-if="!captainSummary(action).length"
              class="m-0 flex items-center gap-2"
            >
              <span class="i-woot-captain size-4 shrink-0" />
              {{ $t('CAPTAIN.ASSIGNMENT.SUMMARY.EACH_INBOX') }}
            </p>
            <p
              v-for="line in captainSummary(action)"
              :key="line.inbox.id"
              class="m-0 flex items-center gap-2"
            >
              <span class="i-woot-captain size-4 shrink-0" />
              {{
                $t(`CAPTAIN.ASSIGNMENT.SUMMARY.${line.status.toUpperCase()}`, {
                  inbox: line.inbox.name,
                  assistant: line.assistant?.name,
                })
              }}
            </p>
          </div>
        </template>
      </AutomationActions>
      <div
        v-if="hasStatusChangingAction"
        class="w-full flex flex-wrap items-center justify-between gap-3 rounded-lg border border-n-weak bg-n-solid-2 p-3"
      >
        <div>
          <p class="m-0 text-sm font-medium text-n-slate-12">
            {{ $t('AUTOMATION.ENFORCE_BUSINESS_RULES.LABEL') }}
          </p>
          <p class="mb-0 mt-1 text-xs text-n-slate-11">
            {{ $t('AUTOMATION.ENFORCE_BUSINESS_RULES.HELP') }}
          </p>
        </div>
        <Switch
          :model-value="automation.enforces_business_rules"
          @update:model-value="
            value => (automation.enforces_business_rules = value)
          "
        />
      </div>
      <!-- Server-side lint findings -->
      <div
        v-if="lintErrors.length || lintWarnings.length"
        role="alert"
        class="w-full flex flex-col gap-2"
      >
        <div
          v-if="lintErrors.length"
          class="rounded-lg border border-n-ruby-8 bg-n-ruby-2 dark:bg-n-ruby-3/20 px-3 py-2.5"
        >
          <p class="text-sm font-medium text-n-ruby-11 mb-1">
            {{ $t('AUTOMATION.LINT.ERRORS_TITLE') }}
          </p>
          <ul class="list-disc ltr:pl-5 rtl:pr-5 space-y-0.5">
            <li
              v-for="(finding, index) in lintErrors"
              :key="`lint-error-${index}`"
              class="text-sm text-n-ruby-11"
            >
              {{ lintMessage(finding) }}
            </li>
          </ul>
        </div>
        <div
          v-if="lintWarnings.length"
          class="rounded-lg border border-n-amber-8 bg-n-amber-2 dark:bg-n-amber-3/20 px-3 py-2.5"
        >
          <p class="text-sm font-medium text-n-amber-11 mb-1">
            {{ $t('AUTOMATION.LINT.WARNINGS_TITLE') }}
          </p>
          <ul class="list-disc ltr:pl-5 rtl:pr-5 space-y-0.5">
            <li
              v-for="(finding, index) in lintWarnings"
              :key="`lint-warning-${index}`"
              class="text-sm text-n-amber-11"
            >
              {{ lintMessage(finding) }}
            </li>
          </ul>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex flex-row justify-end w-full gap-2">
        <NextButton
          faded
          slate
          type="button"
          :label="$t(cancelKey)"
          @click="close"
        />
        <NextButton
          solid
          blue
          type="button"
          :label="$t(submitKey)"
          :disabled="isSaving"
          :is-loading="isSaving"
          @click="emitSaveAutomation"
        />
      </div>
    </template>
  </SidePanel>
</template>
